import React, { useContext } from 'react';
import styled, { keyframes } from 'styled-components';
import { NavLink, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box,
  Button, IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem, MenuList, Paper, Typography, useTheme,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAnglesLeft,
  faAnglesRight,
  faArrowRightFromBracket,
} from '@fortawesome/free-solid-svg-icons';
import meta from '../../const/meta';
import {
  MainIcon,
  LoadIcon,
  ReportsIcon,
  PayersIcon,
  ContractIcon,
  CheckIcon,
  ExtractIcon,
  Logo,
  GroupsUsersIcon,
  Regions,
  EditIcon,
} from '../Styled/Buttons/icons';
import { AppContext } from '../../providers/authProvider';

const rotate = keyframes`
  0% {
    transform: rotate(0deg);
  }

  98% {
    transform: rotate(0deg);
  }
  99% {
    transform: rotate(180deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;
const StyledImg = styled.img`
  height: 75px;
  margin-right: 5px;
  animation: ${rotate} 60s linear infinite;
  max-width: 100%;
`;

const menus = [
  {
    url: '/',
    label: 'Головна',
    src: MainIcon,
  },
  {
    url: '/srv/exchange/',
    label: 'Завантажити виписку',
    src: LoadIcon,
    section: 'load_subscription',
  },
  {
    url: '/srv/loadGRKSubs/',
    label: 'Завантажити виписку ГРК',
    src: LoadIcon,
    section: 'load_subscription',
  },
  {
    url: '/grep/',
    label: 'Звітність',
    src: ReportsIcon,
    section: 'report',
  },
  {
    url: meta.cat.payers.frontend,
    label: 'Платники',
    src: PayersIcon,
    section: 'payers',
  },
  {
    url: meta.doc.contracts.frontend,
    label: 'Довідник договорів',
    src: ContractIcon,
    section: 'contract',
  },
  {
    url: meta.cat.accounts.frontend,
    label: 'Казначейскі рахунки',
    src: CheckIcon,
    section: 'account',
  },
  {
    url: '/treasure_subscriptions/',
    label: 'Виписки',
    src: ExtractIcon,
    section: 'subscription',
  },
  {
    url: meta.other.users.frontend,
    label: 'Користувачі',
    src: GroupsUsersIcon,
    onlyForAdmins: true,
  },
  {
    url: meta.cat.landcategories.frontend,
    label: 'Категорія земель',
    src: Regions,
    onlyForAdmins: true,
    section: 'subscription',
  },
  {
    url: '/household/',
    label: 'Домогосподарства',
    src: Regions,
    section: 'household',
  },
  {
    url: '/accounting_lands/',
    label: 'Облік земельних ділянок',
    src: Regions,
    section: 'contract',
  },
  {
    url: meta.cat.salaries.frontend,
    label: 'Ставки мін з/п',
    onlyForAdmins: true,
  },
  {
    url: '/calcofcontr/',
    label: 'Розрахунок по контрактах',
    src: CheckIcon,
    section: 'contract',
  },
  {
    url: '/srv/',
    label: 'Сервіс',
    src: LoadIcon,
    section: 'service',
  },
  {
    url: '/logica/',
    label: 'Робота з системою Logica',
    src: LoadIcon,
    section: 'logica',
  },
  {
    url: meta.cat.markers.frontend,
    label: 'Маркери',
    src: EditIcon,
    onlyForAdmins: true,
  },
  {
    url: meta.cat.kveds.frontend,
    label: 'КВЕД',
    src: EditIcon,
    onlyForAdmins: true,
  },
  {
    url: '/cat/',
    label: 'Довідники',
    src: EditIcon,
    onlyForAdmins: false,
  },
];

function Menu({
  menuExpanded,
  onToggleMenuExpaned,
}) {
  const {
    currentUser,
    logoutHandler,
  } = useContext(AppContext);
  const theme = useTheme();
  const t = theme.transitions.create(['width']);
  const { pathname } = useLocation();
  return (
    <Paper
      square
      elevation={4}
      component="section"
      sx={{
        transition: t,
        width: menuExpanded ? theme.menu.expanded.width : theme.menu.shrinked.width,
        height: '100vh',
        overflow: 'hidden',
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{
        background: 'linear-gradient(130deg, rgba(204, 230, 255, 0.88), rgba(255, 255, 255, 0.88)), #4281C9',
        display: 'flex',
        flexDirection: 'column',
        borderBottom: 1,
        borderColor: 'lightblue',
      }}
      >
        <Box sx={{ display: 'flex', p: 0.5 }}>
          <StyledImg src={Logo} alt="Logo" />
          <Typography sx={{ display: menuExpanded ? 'block' : 'none' }} variant="subtitle2" color="primary">
            Аналіз доходів бюджету в розрізі платників податків
          </Typography>
        </Box>
        <IconButton
          onClick={onToggleMenuExpaned}
          size="small"
          sx={{ ml: 'auto' }}
          title={menuExpanded ? 'Згорнути' : 'Розгорнути'}
        >
          <FontAwesomeIcon icon={menuExpanded ? faAnglesLeft : faAnglesRight} />
        </IconButton>
      </Box>
      <MenuList sx={{ overflowY: 'auto', overflowX: 'hidden' }}>
        {menus
          .filter((menuItem) => (!menuItem.onlyForAdmins || currentUser.is_superuser)
                && (!menuItem.section
                    || currentUser.available_sections.includes(menuItem.section)))
          .map((menuItem) => (
            <MenuItem
              key={menuItem.url}
              to={menuItem.url}
              exact
              component={NavLink}
              selected={pathname === menuItem.url}
            >
              {menuItem.src && (
              <ListItemIcon>
                <img width="20" src={menuItem.src} alt={menuItem.label} />
              </ListItemIcon>
              )}
              <ListItemText sx={{ whiteSpace: 'break-spaces' }}>{menuItem.label}</ListItemText>
            </MenuItem>
          ))}
      </MenuList>
      <Box sx={{ width: 1, pt: 2, textAlign: 'center' }}>
        {menuExpanded ? (
          <NavLink to="/">
            <Button
              onClick={logoutHandler}
              variant="outlined"
              color="primary"
              startIcon={<FontAwesomeIcon icon={faArrowRightFromBracket} />}
              fullWidth
            >
              Вихід
            </Button>
          </NavLink>
        ) : (
          <IconButton
            onClick={logoutHandler}
          >
            <FontAwesomeIcon icon={faArrowRightFromBracket} />
          </IconButton>

        )}
      </Box>
    </Paper>
  );
}

Menu.propTypes = {
  menuExpanded: PropTypes.bool.isRequired,
  onToggleMenuExpaned: PropTypes.func.isRequired,
};

export default Menu;
